import React, { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { FormHandles, SubmitHandler } from '@unform/core'
import { Form } from '@unform/web'
import SendIcon from 'assets/icons/webapp_send.svg'
import SendIconFloating from 'assets/icons/ai_chat_send.svg'
import AddIcon from 'assets/icons/webapp_upload.svg'
import { trackPromise } from 'react-promise-tracker'
import { ApiError } from 'services/ApiService'
import FileUploadService from 'services/FileUploadService'
import Spinner from 'components/elements/Spinner'
import { InputButtonWrapper, IconWrapper, HiddenInput } from './ChatMessageForm.css'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { ContentPrefixes } from 'lib/constants/ContentPrefixes'
import PDFIcon from 'assets/icons/webapp_pdf.svg'
import { useToggleState } from 'lib/hooks/useToggleState'
import AddCircleIcon from 'assets/icons/webapp_add.svg'
import ChooseKnowDialog from 'components/feature/Know/KnowSelectionModal'
import _useTranslation from 'lib/hooks/_useTranslation'
import Textarea from 'components/elements/Inputs/Textarea'

const loadingAreas = {
    upload: 'fileUpload',
}

interface FormData {
    message: string
    chatUuid: string
}

interface Props {
    onSendMessage: (message: string, filename: string | null, knowUuid: string | null) => void
    contentPrefix: ContentPrefixes
    isDarkTheme?: boolean
    hideExtraButtons?: boolean
}

const ChatMessageForm = (props: Props) => {
    const [isUploading, setIsUploading] = useState(false)
    const [isUploadingPDF, setIsUploadingPDF] = useState(false)
    const sendMessageFormRef = useRef<FormHandles>(null)
    const { t: tCommon } = _useTranslation('common')
    const [showChooseKnowDialog, toggleChooseKnowDialog] = useToggleState(false)
    const baseHeight = props.hideExtraButtons ? 80 : 65
    const [textareaHeight, setTextareaHeight] = useState(baseHeight)

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textarea = e.target
        textarea.style.height = `${baseHeight}px`

        const newHeight = textarea.value === '' ? baseHeight : Math.min(textarea.scrollHeight, 400)

        setTextareaHeight(newHeight)
        textarea.style.height = `${newHeight}px`
    }

    const handleSendMessage: SubmitHandler<FormData> = (data) => {
        sendMessageFormRef.current?.clearField('message')
        setTextareaHeight(baseHeight)
        props.onSendMessage(data.message, null, null)
    }

    const sendMessage = () => {
        sendMessageFormRef.current?.submitForm()
    }

    const resetInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.value = ''
        setIsUploading(false)
        setIsUploadingPDF(false)
    }

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, isPDF?: boolean) => {
        isPDF ? setIsUploadingPDF(true) : setIsUploading(true)
        const files = event.target.files

        if (!files) {
            resetInput(event)
            return
        }
        const maxFileSize = 25 * 1000000

        const file = files[0]
        if (!file) {
            resetInput(event)
            return
        }
        if (isPDF && file.type !== 'application/pdf') {
            toast.error('Lade bitte deine Datei als PDF hoch!')
            resetInput(event)
            return
        }
        if (!isPDF && file.type !== 'image/jpeg' && file.type !== 'image/png') {
            toast.error('Lade bitte deine Datei als PNG/JPG hoch!')
            resetInput(event)
            return
        }

        if (file.size > maxFileSize) {
            toast.error('Die Datei ist leider zu groß. Die maximale Dateigröße ist 25 MB.')
            resetInput(event)
            return
        }

        trackPromise(
            FileUploadService.upload(file, props.contentPrefix)
                .then((data) => {
                    props.onSendMessage('', data.filename, null)
                    resetInput(event)
                })
                .catch((error) => {
                    resetInput(event)
                    setIsUploading(false)
                    if (error instanceof ApiError) {
                        if (error.type === ErrorTypes.InvalidMIMEType) {
                            toast.error(`Lade bitte deine Datei als ${isPDF ? 'PDF' : 'PNG/JPG'} hoch!`)
                        } else {
                            error.handleUnknownError(tCommon, 'file.upload')
                        }
                    } else {
                        throw error
                    }
                }),
            loadingAreas.upload
        )
    }

    const onKnowChoosen = (knowUuid: string) => {
        props.onSendMessage('', null, knowUuid)
        toggleChooseKnowDialog()
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && props.hideExtraButtons) {
            if (!event.shiftKey) {
                event.preventDefault()
                sendMessage()
            }
        }
    }

    return (
        <Form ref={sendMessageFormRef} onSubmit={handleSendMessage}>
            <InputButtonWrapper hideExtraButtons={props.hideExtraButtons}>
                <Textarea
                    name="message"
                    placeholder="..."
                    hideLabel
                    modern
                    rows={1}
                    height={textareaHeight}
                    darkTheme={props.isDarkTheme}
                    onKeyDown={handleKeyDown}
                    onChange={handleTextareaChange}
                    style={{ height: textareaHeight }}
                />
                {!props.hideExtraButtons ? (
                    <div className="actions">
                        <IconWrapper onClick={sendMessage}>
                            <div className="send">
                                <SendIcon />
                            </div>
                        </IconWrapper>
                        <IconWrapper onClick={toggleChooseKnowDialog}>
                            <div>
                                <AddCircleIcon />
                            </div>
                        </IconWrapper>
                        <HiddenInput
                            accept="image/png, image/jpeg"
                            id="contained-button-file"
                            type="file"
                            onChange={handleFileUpload}
                        />
                        <IconWrapper>
                            <label htmlFor="contained-button-file">
                                <div>{isUploading ? <Spinner forceShow /> : <AddIcon />}</div>
                            </label>
                        </IconWrapper>
                        {props.contentPrefix === ContentPrefixes.ChatAttachment ? (
                            <>
                                <HiddenInput
                                    accept="application/pdf"
                                    id="contained-button-pdf"
                                    type="file"
                                    onChange={(e) => handleFileUpload(e, true)}
                                />
                                <IconWrapper>
                                    <label htmlFor="contained-button-pdf">
                                        <div>{isUploadingPDF ? <Spinner forceShow /> : <PDFIcon />}</div>
                                    </label>
                                </IconWrapper>
                            </>
                        ) : null}
                    </div>
                ) : (
                    <IconWrapper onClick={sendMessage} floating>
                        <div className="send">
                            <SendIconFloating />
                        </div>
                    </IconWrapper>
                )}
            </InputButtonWrapper>
            {showChooseKnowDialog ? (
                <ChooseKnowDialog
                    open={showChooseKnowDialog}
                    handleClose={toggleChooseKnowDialog}
                    onKnowChoosen={onKnowChoosen}
                />
            ) : null}
        </Form>
    )
}

export default ChatMessageForm
